import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { formatMoney, formatDate } from "../lib/format";
import { useConfirm, useAlert } from "../components/Confirm";

/**
 * Case-by-case full editor.
 *
 * Lives at /case/:id. The estimate is already created (see CaseNew.tsx);
 * this page handles the rich input form, live calc preview, save draft,
 * submit for approval, and recall / delete flows.
 *
 * Live preview: every input change debounces a stateless POST to
 * /api/pricing/case/calculate so the breakdown updates as the user types.
 * Hitting "Save & recalculate" persists the inputs + the calc to the DB.
 */
const SHARED_MODES = [
  "Shared day and night support",
  "Shared day only",
  "None",
] as const;

const ACUITY_OPTIONS  = ["Low", "Medium", "High", "Severe", "Complex"] as const;
const DIVISION_OPTIONS = ["Group", "Liaise Life"] as const;

type SharedMode = typeof SHARED_MODES[number];

interface CaseFormState {
  shared_hours_mode: SharedMode;
  core_day_hours: number;
  core_night_hours: number;
  core_sleep_in_shifts: number;
  ones_day_hours: number;
  ones_night_hours: number;
  twos_day_hours: number;
  twos_night_hours: number;
  dedicated_night_hours: number;
  additional_sleep_in_shifts: number;
  contribution_target_pct: number;     // 0..1
  hourly_fee_override: number | null;
  // Workbook context fields (not in the calc, but stored on pricing.estimate_case)
  acuity:   string;
  division: string;
  notes:    string;
}

export function CaseDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const est   = useQuery({ queryKey: ["est-case", id], queryFn: () => api.getCase(id) });
  const me    = useQuery({ queryKey: ["me"], queryFn: api.me });
  const homes   = useQuery({ queryKey: ["homes"],   queryFn: api.homes });
  const funders = useQuery({ queryKey: ["funders"], queryFn: api.funders });
  const confirm = useConfirm();
  const alertModal = useAlert();

  // Editable header (mirrors the workbook's home / funder / initials inputs)
  const [homeId,   setHomeId]   = useState<number | "">("");
  const [funderId, setFunderId] = useState<number | "">("");
  const [pwsInitials, setPwsInitials] = useState("");
  useEffect(() => {
    if (!est.data) return;
    setHomeId(est.data.home_id ?? "");
    setFunderId(est.data.funder_id ?? "");
    setPwsInitials(est.data.pws_initials ?? "");
  }, [est.data]);

  const [form, setForm] = useState<CaseFormState>({
    shared_hours_mode: "Shared day and night support",
    core_day_hours: 0,
    core_night_hours: 0,
    core_sleep_in_shifts: 0,
    ones_day_hours: 0,
    ones_night_hours: 0,
    twos_day_hours: 0,
    twos_night_hours: 0,
    dedicated_night_hours: 0,
    additional_sleep_in_shifts: 0,
    contribution_target_pct: 0.10,
    hourly_fee_override: null,
    acuity:   "",
    division: "",
    notes:    "",
  });

  // Hydrate form from server data
  useEffect(() => {
    const cd = est.data?.case_data;
    if (!cd) return;
    setForm({
      shared_hours_mode:          (cd.shared_hours_mode ?? "Shared day and night support") as SharedMode,
      core_day_hours:             Number(cd.core_day_hours),
      core_night_hours:           Number(cd.core_night_hours),
      core_sleep_in_shifts:       Number(cd.core_sleep_in_shifts),
      ones_day_hours:             Number(cd.ones_day_hours),
      ones_night_hours:           Number(cd.ones_night_hours),
      twos_day_hours:             Number(cd.twos_day_hours),
      twos_night_hours:           Number(cd.twos_night_hours),
      dedicated_night_hours:      Number(cd.dedicated_night_hours),
      additional_sleep_in_shifts: Number(cd.additional_sleep_in_shifts),
      contribution_target_pct:    Number(cd.contribution_target_pct),
      hourly_fee_override:        cd.hourly_fee_override != null ? Number(cd.hourly_fee_override) : null,
      acuity:                     cd.acuity   ?? "",
      division:                   cd.division ?? "",
      notes:                      cd.notes    ?? "",
    });
  }, [est.data]);

  // Live calc preview (debounced). Uses the editable homeId state, not est.data,
  // so flipping the home in the dropdown re-runs the breakdown immediately.
  const [preview, setPreview] = useState<any>(null);
  const [calcError, setCalcError] = useState<string | null>(null);
  useEffect(() => {
    if (homeId === "") return;
    const t = setTimeout(async () => {
      try {
        const result = await api.calculateCase({ home_id: Number(homeId), ...form });
        setPreview(result);
        setCalcError(null);
      } catch (e) {
        setCalcError((e as Error).message);
        setPreview(null);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [homeId, form]);

  const buildPayload = () => ({
    home_id:     homeId === "" ? null : Number(homeId),
    funder_id:   funderId === "" ? null : Number(funderId),
    pws_initials: pwsInitials || "??",
    ...form,
  });

  const saveAndRecalc = useMutation({
    mutationFn: () => api.updateCase(id, buildPayload()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["est-case", id] }),
  });

  const submit = useMutation({
    mutationFn: async () => {
      await api.updateCase(id, buildPayload());
      return api.estimateAction(id, "submit");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["est-case", id] }),
  });

  async function onDelete() {
    const ok = await confirm({
      title: "Delete this draft?",
      body: "The case quote and all its inputs will be removed. This cannot be undone.",
      danger: true,
      confirmLabel: "Delete draft",
    });
    if (!ok) return;
    await api.deleteCase(id);
    navigate("/");
  }

  async function onRecall() {
    const ok = await confirm({
      title: "Recall this estimate?",
      body: "Pulls the quote back from approval so you can edit it again.",
      confirmLabel: "Recall",
    });
    if (!ok) return;
    try {
      await api.estimateAction(id, "recall");
      await qc.invalidateQueries({ queryKey: ["est-case", id] });
    } catch (err) {
      await alertModal({ title: "Couldn't recall", body: String(err) });
    }
  }

  if (est.isLoading) return <div className="text-slate-500">Loading...</div>;
  if (!est.data)     return <div className="text-red-600">Not found.</div>;
  const e = est.data;
  const editable = e.status === "draft";
  const isApprover = !!(me.data?.roles?.approver || me.data?.roles?.admin);

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* ---------- Left column: inputs ---------- */}
      <div className="space-y-6">
        <div>
          <Link to="/" className="text-xs text-slate-500 hover:text-slate-700">← Back</Link>
          <div className="flex items-center gap-3 mt-2">
            <h1 className="text-2xl font-semibold text-liaise-navy">{e.request_number}</h1>
            <StatusPill status={e.status} />
          </div>
        </div>

        <Section title="Placement">
          <div className="grid grid-cols-3 gap-3">
            <Row label="Home">
              <select
                className="input"
                disabled={!editable}
                value={homeId}
                onChange={(ev) => setHomeId(ev.target.value === "" ? "" : Number(ev.target.value))}
              >
                <option value="">Select a home...</option>
                {/* Only show homes with a cost stack for the active FY, plus the
                    estimate's current home (even if it's now without one) so
                    we don't silently flip the selection. */}
                {(homes.data?.homes ?? [])
                  .filter((h: any) => h.has_cost_stack || h.id === e.home_id)
                  .map((h: any) => (
                    <option key={h.id} value={h.id}>{h.name}{!h.has_cost_stack ? " (no cost stack)" : ""}</option>
                  ))}
              </select>
            </Row>
            <Row label="PWS initials">
              <input
                className="input"
                maxLength={20}
                disabled={!editable}
                value={pwsInitials}
                onChange={(ev) => setPwsInitials(ev.target.value.toUpperCase())}
              />
            </Row>
            <Row label="Funder">
              <select
                className="input"
                disabled={!editable}
                value={funderId}
                onChange={(ev) => setFunderId(ev.target.value === "" ? "" : Number(ev.target.value))}
              >
                <option value="">Select a funder...</option>
                {(funders.data?.funders ?? []).map((f: any) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </Row>
          </div>
        </Section>

        <Section title="Placement context">
          <div className="grid grid-cols-2 gap-3">
            <Row label="Acuity">
              <select
                className="input"
                disabled={!editable}
                value={form.acuity}
                onChange={(ev) => setForm({ ...form, acuity: ev.target.value })}
              >
                <option value="">Not set</option>
                {ACUITY_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </Row>
            <Row label="Division">
              <select
                className="input"
                disabled={!editable}
                value={form.division}
                onChange={(ev) => setForm({ ...form, division: ev.target.value })}
              >
                <option value="">Inherit from home</option>
                {DIVISION_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </Row>
          </div>
          <Row label="Notes (optional, internal only)">
            <textarea
              className="input"
              rows={2}
              disabled={!editable}
              placeholder="Anything James needs to know on review (referral context, urgency, etc.)"
              value={form.notes}
              onChange={(ev) => setForm({ ...form, notes: ev.target.value })}
            />
          </Row>
        </Section>

        <Section title="Shared / core hours">
          <Row label="Mode">
            <select
              className="input"
              disabled={!editable}
              value={form.shared_hours_mode}
              onChange={(ev) => setForm({ ...form, shared_hours_mode: ev.target.value as SharedMode })}
            >
              {SHARED_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Row>
          {form.shared_hours_mode !== "None" && (
            <div className="grid grid-cols-3 gap-3">
              <NumInput label="Core day hrs / wk"    value={form.core_day_hours}       editable={editable} onChange={(v) => setForm({ ...form, core_day_hours: v })} />
              {form.shared_hours_mode === "Shared day and night support" && (
                <>
                  <NumInput label="Core night hrs / wk"  value={form.core_night_hours}     editable={editable} onChange={(v) => setForm({ ...form, core_night_hours: v })} />
                  <NumInput label="Core sleep-ins / wk"  value={form.core_sleep_in_shifts} editable={editable} onChange={(v) => setForm({ ...form, core_sleep_in_shifts: v })} />
                </>
              )}
            </div>
          )}
        </Section>

        <Section title="1:1 dedicated hours">
          <div className="grid grid-cols-2 gap-3">
            <NumInput label="1:1 day hrs / wk"   value={form.ones_day_hours}   editable={editable} onChange={(v) => setForm({ ...form, ones_day_hours: v })} />
            <NumInput label="1:1 night hrs / wk" value={form.ones_night_hours} editable={editable} onChange={(v) => setForm({ ...form, ones_night_hours: v })} />
          </div>
        </Section>

        <Section title="2:1 hours">
          <div className="grid grid-cols-2 gap-3">
            <NumInput label="2:1 day hrs / wk"   value={form.twos_day_hours}   editable={editable} onChange={(v) => setForm({ ...form, twos_day_hours: v })} />
            <NumInput label="2:1 night hrs / wk" value={form.twos_night_hours} editable={editable} onChange={(v) => setForm({ ...form, twos_night_hours: v })} />
          </div>
        </Section>

        <Section title="Dedicated night + sleep-ins">
          <div className="grid grid-cols-2 gap-3">
            <NumInput label="Dedicated night hrs / wk" value={form.dedicated_night_hours}      editable={editable} onChange={(v) => setForm({ ...form, dedicated_night_hours: v })} />
            <NumInput label="Extra sleep-ins / wk"     value={form.additional_sleep_in_shifts} editable={editable} onChange={(v) => setForm({ ...form, additional_sleep_in_shifts: v })} />
          </div>
        </Section>

        <Section title="Contribution target & fee override">
          <div className="grid grid-cols-2 gap-3">
            <Row label="Target margin (% of cost)">
              <input
                type="number" step={0.5} min={0} max={50}
                className="input w-32"
                disabled={!editable}
                value={form.contribution_target_pct * 100}
                onChange={(ev) => setForm({ ...form, contribution_target_pct: Number(ev.target.value || 0) / 100 })}
              />
              <div className="text-xs text-slate-500 mt-1">Default 10%. Liaise policy caps at 28%.</div>
            </Row>
            <Row label="Hourly fee override (£/hr)">
              <input
                type="number" step={0.5} min={0}
                className="input w-32"
                disabled={!editable}
                placeholder="Optional"
                value={form.hourly_fee_override ?? ""}
                onChange={(ev) => setForm({ ...form, hourly_fee_override: ev.target.value === "" ? null : Number(ev.target.value) })}
              />
              <div className="text-xs text-slate-500 mt-1">Force a specific hourly fee instead of deriving from the cost stack.</div>
            </Row>
          </div>
        </Section>

        {/* Derived totals — matches the workbook's "Total hours" summary row */}
        <Section title="Totals (as you type)">
          <div className="grid grid-cols-4 gap-3 text-sm">
            <DerivedCell label="Total day hrs / wk"   value={form.core_day_hours + form.ones_day_hours + form.twos_day_hours} />
            <DerivedCell label="Total night hrs / wk" value={form.core_night_hours + form.ones_night_hours + form.twos_night_hours + form.dedicated_night_hours} />
            <DerivedCell label="Total sleep-ins / wk" value={form.core_sleep_in_shifts + form.additional_sleep_in_shifts} />
            <DerivedCell label="Total hrs / wk" value={
              form.core_day_hours + form.ones_day_hours + form.twos_day_hours +
              form.core_night_hours + form.ones_night_hours + form.twos_night_hours + form.dedicated_night_hours
            } />
          </div>
        </Section>

        {editable ? (
          <div className="flex items-center gap-3">
            <button
              className="px-4 py-2 rounded-lg bg-liaise-green text-white text-sm font-medium disabled:opacity-50"
              disabled={saveAndRecalc.isPending}
              onClick={() => saveAndRecalc.mutate()}
            >
              {saveAndRecalc.isPending ? "Saving..." : "Save & recalculate"}
            </button>
            <button
              className="px-4 py-2 rounded-lg bg-liaise-navy text-white text-sm font-medium disabled:opacity-50"
              disabled={submit.isPending}
              onClick={() => submit.mutate()}
            >
              {submit.isPending ? "Submitting..." : "Submit for approval"}
            </button>
            <button onClick={onDelete} className="px-4 py-2 rounded-lg text-sm text-red-600 hover:underline">Delete draft</button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            {e.status === "submitted" && e.claimant_entra_oid === me.data?.oid && (
              <button onClick={onRecall} className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-sm">Recall</button>
            )}
            {isApprover && (e.status === "submitted") && (
              <Link to={`/approvals/${id}`} className="px-4 py-2 rounded-lg bg-liaise-blue text-white text-sm font-medium">
                Open in approvals →
              </Link>
            )}
          </div>
        )}
      </div>

      {/* ---------- Right column: live preview ---------- */}
      <div className="space-y-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden sticky top-4">
          <div className="px-6 py-4 bg-liaise-navy text-white">
            <div className="text-xs uppercase tracking-wider opacity-80">
              {((homes.data?.homes ?? []).find((h: any) => h.id === Number(homeId))?.name) ?? e.home_name ?? "-"}
              {" · "}
              {pwsInitials || e.pws_initials || "??"}
            </div>
            <div className="text-2xl font-semibold mt-1">
              {preview ? formatMoney(preview.totals.annual_fee) : formatMoney(Number(e.total_amount ?? 0))} <span className="text-sm opacity-80">/ year</span>
            </div>
            <div className="text-xs opacity-80 mt-1">
              {preview ? formatMoney(preview.totals.target_fee_per_week) : "-"} per week target fee
            </div>
          </div>

          {calcError && (
            <div className="px-6 py-3 bg-liaise-red/10 text-red-700 text-sm">
              Calc error: {calcError}
            </div>
          )}

          {preview && (
            <div className="p-6 text-sm space-y-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">Cost breakdown (per week)</div>
                <BreakdownLine label="Management"       value={preview.lines.mgmt_per_week} />
                <BreakdownLine label="Overheads"        value={preview.lines.overheads_per_week} />
                <BreakdownLine label="Central support"  value={preview.lines.central_support_per_week} />
                <BreakdownLine label="Property"         value={preview.lines.property_per_week} />
                <BreakdownLine label="Regional support" value={preview.lines.regional_per_week} />
                {preview.lines.shared_day_per_week   > 0 && <BreakdownLine label="Shared day support"   value={preview.lines.shared_day_per_week} />}
                {preview.lines.shared_night_per_week > 0 && <BreakdownLine label="Shared night support" value={preview.lines.shared_night_per_week} />}
                {preview.lines.ones_per_week         > 0 && <BreakdownLine label="1:1 staffing"          value={preview.lines.ones_per_week} />}
                {preview.lines.twos_per_week         > 0 && <BreakdownLine label="2:1 staffing"          value={preview.lines.twos_per_week} />}
                {preview.lines.dedicated_per_week    > 0 && <BreakdownLine label="Dedicated night"       value={preview.lines.dedicated_per_week} />}
                {preview.lines.sleep_in_per_week     > 0 && <BreakdownLine label="Sleep-ins"             value={preview.lines.sleep_in_per_week} />}
                {preview.lines.ha_income_per_week   !== 0 && <BreakdownLine label="HA income (offset)"  value={-Math.abs(preview.lines.ha_income_per_week)} />}
                <div className="flex justify-between items-center border-t border-slate-200 mt-2 pt-2 font-semibold text-liaise-navy">
                  <span>Total cost / wk</span>
                  <span className="tabular-nums">{formatMoney(preview.totals.total_cost_per_week)}</span>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600 space-y-1">
                <div>Blended hourly rate: <b>{formatMoney(preview.rates.blended_hourly)}</b> ({Math.round(preview.rates.agency_pct * 100)}% agency)</div>
                <div>Sleep-in all-in: <b>{formatMoney(preview.rates.all_in_sleep_in_night)}</b></div>
                <div>Implied margin: <b>{(preview.totals.implied_margin_pct * 100).toFixed(1)}%</b></div>
                <div>Fiscal year: <b>{preview.fiscal_year}</b></div>
              </div>

              {preview.warnings?.length > 0 && (
                <div className="bg-liaise-orange/10 rounded-lg p-3 text-xs text-orange-800 space-y-1">
                  {preview.warnings.map((w: string, i: number) => <div key={i}>⚠ {w}</div>)}
                </div>
              )}

              <div className="text-[10px] text-slate-400">
                Reference data last refreshed:
                {preview.sources?.home_cost_stack_synced_at && <> cost stack {formatDate(preview.sources.home_cost_stack_synced_at)} ·</>}
                {preview.sources?.group_rates_synced_at      && <> group rates {formatDate(preview.sources.group_rates_synced_at)}</>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Funder-facing preview card — full width below the editor.
          Uses the saved case_data so it matches what the funder eventually sees by email. */}
      {e.case_data && (
        <div className="lg:col-span-2">
          <CaseOutput estimate={e} />
        </div>
      )}

      <style>{`.input{display:block;width:100%;padding:.5rem .75rem;border:1px solid rgb(226 232 240);border-radius:.5rem;font-size:.875rem;background:white}`}</style>
    </div>
  );
}

/* ---------------- funder-facing preview card ---------------- */

export function CaseOutput({ estimate }: { estimate: any }) {
  const e = estimate;
  const cd = e.case_data ?? {};
  const pence = (k: string) => Number(cd[k] ?? 0) / 100;
  const annualFee = pence("annual_fee_pence");
  const adjustments: Array<{ amount_pence: number; reason?: string | null }> = e.adjustments ?? [];
  const adjTotal = adjustments.reduce((s, a) => s + Number(a.amount_pence ?? 0) / 100, 0);
  const finalTotal = annualFee + adjTotal;

  const isApproved = e.status === "approved";
  const isSubmitted = e.status === "submitted";
  const isDraft = e.status === "draft";

  const lines = [
    { label: "Management",       value: pence("mgmt_per_week_pence") },
    { label: "Overheads",        value: pence("overheads_per_week_pence") },
    { label: "Central support",  value: pence("central_support_per_week_pence") },
    { label: "Property",         value: pence("property_per_week_pence") },
    { label: "Regional support", value: pence("regional_per_week_pence") },
  ].filter((l) => l.value !== 0);

  const staffingLines = [
    { label: "Shared day support",   value: pence("shared_day_per_week_pence") },
    { label: "Shared night support", value: pence("shared_night_per_week_pence") },
    { label: "1:1 staffing",         value: pence("ones_per_week_pence") },
    { label: "2:1 staffing",         value: pence("twos_per_week_pence") },
    { label: "Dedicated night",      value: pence("dedicated_per_week_pence") },
    { label: "Sleep-ins",            value: pence("sleep_in_per_week_pence") },
  ].filter((l) => l.value !== 0);

  const haIncome = pence("ha_income_per_week_pence");

  return (
    <div className="rounded-2xl border-2 overflow-hidden"
         style={{ borderColor: isApproved ? "#3DAE2B" : isSubmitted ? "#FF8300" : "#cbd5e1", background: "#ffffff" }}>
      {!isApproved && (
        <div className={`px-6 py-3 text-sm font-semibold uppercase tracking-wider flex items-center gap-2 ${
          isSubmitted ? "bg-liaise-orange/15 text-orange-800" : "bg-slate-100 text-slate-600"
        }`}>
          <span className="inline-block w-2 h-2 rounded-full bg-current animate-pulse" />
          {isDraft && "Preview, not yet submitted. Funder will only see this once approved."}
          {isSubmitted && "Submitted. Awaiting approval from Commercial Finance."}
        </div>
      )}
      {isApproved && (
        <div className="px-6 py-3 text-sm font-semibold uppercase tracking-wider bg-liaise-green/15 text-emerald-800 flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-emerald-600" />
          Approved, ready to forward to the funder.
        </div>
      )}
      <div className="p-8">
        <div className="flex items-start justify-between gap-4 border-b-2 border-liaise-navy pb-5 mb-5">
          <img src="/liaise-wordmark-navy.svg" alt="Liaise" className="h-9 w-auto" />
          <div className="text-right">
            <div className="text-xs tracking-wider text-slate-500 uppercase">Case-by-case staffing cost estimate</div>
            <div className="text-xl font-semibold text-liaise-navy mt-1">
              {formatDate(e.start_date)} – {formatDate(e.end_date)}
            </div>
            <div className="text-xs text-slate-500 mt-1">Ref: {e.request_number}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-4 gap-x-6 mb-6 text-sm">
          <Meta label="PWS initials"  value={e.pws_initials || "-"} />
          <Meta label="Home"          value={e.home_name || "-"} />
          <Meta label="Funder"        value={e.funder_name || "-"} />
          <Meta label="Mode"          value={cd.shared_hours_mode || "-"} />
          <Meta label="Margin target" value={cd.contribution_target_pct != null ? `${(Number(cd.contribution_target_pct) * 100).toFixed(1)}%` : "-"} />
          <Meta label="Prepared by"   value={e.claimant_display_name || "-"} />
        </div>

        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-liaise-navy text-white text-left">
              <th className="px-4 py-3 font-semibold">Cost build-up (per week)</th>
              <th className="px-4 py-3 font-semibold text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            <SubHeader text="Management & overheads" />
            {lines.map((l) => <CostRow key={l.label} label={l.label} total={l.value} />)}
            {staffingLines.length > 0 && <SubHeader text="Staffing" />}
            {staffingLines.map((l) => <CostRow key={l.label} label={l.label} total={l.value} />)}
            {haIncome !== 0 && <CostRow label="HA income (offset)" total={-Math.abs(haIncome)} />}
            <tr className="bg-slate-50">
              <td className="px-4 py-3 font-semibold text-liaise-navy">Total cost per week</td>
              <td className="px-4 py-3 text-right font-bold text-liaise-navy tabular-nums">{formatMoney(pence("total_cost_per_week_pence"))}</td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-slate-700">
                Target fee per week
                <span className="text-xs text-slate-500 ml-2">{cd.contribution_target_pct != null ? `(${(Number(cd.contribution_target_pct) * 100).toFixed(1)}% margin)` : ""}</span>
              </td>
              <td className="px-4 py-3 text-right font-semibold tabular-nums">{formatMoney(pence("target_fee_per_week_pence"))}</td>
            </tr>
            {adjustments.length > 0 && <SubHeader text="Adjustment" />}
            {adjustments.map((a, idx) => (
              <tr key={idx} className="border-t border-slate-100">
                <td className="px-4 py-3">{a.reason || "Adjustment"}</td>
                <td className="px-4 py-3 text-right font-semibold tabular-nums">{formatMoney(Number(a.amount_pence ?? 0) / 100)}</td>
              </tr>
            ))}
            <tr className="bg-liaise-navy text-white">
              <td className="px-4 py-3 font-semibold uppercase tracking-wider text-sm">Annual fee</td>
              <td className="px-4 py-3 font-semibold text-right tabular-nums">{formatMoney(finalTotal)}</td>
            </tr>
          </tbody>
        </table>

        <p className="text-xs text-slate-500 mt-5 leading-relaxed">
          <b>Note:</b> Figures are based on the inputs entered in the Liaise Pricing tool against this person we support and home. Reference cost data is drawn from current management, overhead and regional cost stacks. Figures are exclusive of VAT unless otherwise stated. Subject to formal confirmation.
        </p>
      </div>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="text-liaise-navy font-semibold mt-1">{value}</div>
    </div>
  );
}

function SubHeader({ text }: { text: string }) {
  return (
    <tr className="bg-slate-50">
      <td colSpan={2} className="px-4 py-2 text-xs uppercase tracking-wider text-right text-liaise-navy font-semibold">{text}</td>
    </tr>
  );
}

function CostRow({ label, total }: { label: string; total: number }) {
  return (
    <tr className="border-b border-slate-100">
      <td className="px-4 py-3">{label}</td>
      <td className="px-4 py-3 text-right font-semibold tabular-nums">{formatMoney(total)}</td>
    </tr>
  );
}

/* ---------------- helpers ---------------- */

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
      <h2 className="text-sm font-semibold text-liaise-navy uppercase tracking-wider mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1 font-medium">{label}</div>
      {children}
    </label>
  );
}

function NumInput({ label, value, editable, onChange }: { label: string; value: number; editable: boolean; onChange: (n: number) => void }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1 font-medium">{label}</div>
      <input
        type="number" step={0.5} min={0}
        className="input"
        disabled={!editable}
        value={value}
        onChange={(e) => onChange(Number(e.target.value || 0))}
      />
    </label>
  );
}

function DerivedCell({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-slate-50 rounded-lg px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="text-base font-semibold text-liaise-navy tabular-nums">{value.toFixed(1)}</div>
    </div>
  );
}

function BreakdownLine({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex justify-between items-center py-1.5 border-b border-slate-100">
      <span className="text-slate-700">{label}</span>
      <span className="tabular-nums text-slate-800">{formatMoney(value)}</span>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const styles: Record<string, string> = {
    draft:     "bg-slate-100 text-slate-700",
    submitted: "bg-liaise-orange/15 text-orange-800",
    approved:  "bg-liaise-green/15 text-emerald-800",
    rejected:  "bg-liaise-red/10 text-red-700",
    recalled:  "bg-slate-100 text-slate-500",
    sent:      "bg-liaise-blue/15 text-liaise-blue",
  };
  return (
    <span className={`inline-block text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${styles[status] ?? "bg-slate-100 text-slate-600"}`}>
      {status}
    </span>
  );
}
